import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronDown, Loader2, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Loading, PageHead, useAdmin } from "./ui";
import { FreshaSync } from "./FreshaSync";
import { ExcelTools } from "./DataTools";

const MoveTo = ({ targets, onMove, tid }) => (
  <select className="ad-svc-move" value="" onChange={(e) => e.target.value !== "" && onMove(Number(e.target.value))} aria-label="Move to another category" data-testid={`${tid}-move`}>
    <option value="">Move to…</option>
    {targets.map(([j, label]) => <option key={j} value={j}>{label || "New category"}</option>)}
  </select>
);

const Item = ({ it, onChange, onRemove, tid, targets, onMove }) => (
  <div className="ad-svc" data-testid={tid}>
    <input className="ad-svc-name" value={it.name} onChange={(e) => onChange({ ...it, name: e.target.value })} placeholder="Service name" data-testid={`${tid}-name`} />
    <input value={it.duration} onChange={(e) => onChange({ ...it, duration: e.target.value })} placeholder="Duration" data-testid={`${tid}-duration`} />
    <input type="number" min="0" step="0.01" value={it.price} onChange={(e) => onChange({ ...it, price: e.target.value === "" ? "" : Number(e.target.value) })} placeholder="Price" data-testid={`${tid}-price`} />
    <label className="ad-check"><input type="checkbox" checked={it.from_price} onChange={(e) => onChange({ ...it, from_price: e.target.checked })} data-testid={`${tid}-from`} /> From</label>
    <input className="ad-svc-fresha" type="url" value={it.fresha_url || ""} onChange={(e) => onChange({ ...it, fresha_url: e.target.value.trim() })} placeholder="Fresha link for this service (optional — main Fresha page used if empty)" data-testid={`${tid}-fresha`} />
    <MoveTo targets={targets} onMove={onMove} tid={tid} />
    <button type="button" className="ad-icon-btn" onClick={onRemove} aria-label="Remove service" data-testid={`${tid}-remove`}><Trash2 size={14} /></button>
  </div>
);

const Category = ({ c, onChange, onRemove, open, toggle, targets, onMove }) => (
  <section className="ad-card ad-section" data-testid={`svc-cat-${c.id}`}>
    <button type="button" className="ad-section-head" onClick={toggle} aria-expanded={open} data-testid={`svc-cat-${c.id}-toggle`}>
      {c.label || "New category"} <small>{c.items.length} services</small><ChevronDown size={18} />
    </button>
    {open && (
      <div className="ad-fields">
        <div className="ad-svc-cat">
          <div className="field"><label>Category name</label><input value={c.label} onChange={(e) => onChange({ ...c, label: e.target.value })} data-testid={`svc-cat-${c.id}-label`} /></div>
          <div className="field"><label>Id</label><input value={c.id} onChange={(e) => onChange({ ...c, id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} data-testid={`svc-cat-${c.id}-id`} /></div>
        </div>
        <div className="ad-svc-head"><span>Service</span><span>Duration</span><span>Price (BSD)</span><span /></div>
        {c.items.map((it, i) => (
          <Item key={i} it={it} tid={`svc-${c.id}-${i}`} onChange={(x) => onChange({ ...c, items: c.items.map((o, j) => (j === i ? x : o)) })}
            onRemove={() => onChange({ ...c, items: c.items.filter((_, j) => j !== i) })} targets={targets} onMove={(to) => onMove(i, to)} />
        ))}
        <div className="ad-svc-actions">
          <button type="button" className="btn light" onClick={() => onChange({ ...c, items: [...c.items, { name: "", duration: "", price: 0, from_price: false }] })} data-testid={`svc-cat-${c.id}-add-item`}><Plus size={14} /> Add service</button>
          <button type="button" className="btn light ad-danger" onClick={onRemove} data-testid={`svc-cat-${c.id}-remove`}><Trash2 size={14} /> Delete category</button>
        </div>
      </div>
    )}
  </section>
);

export default function ServicesEditor() {
  const { data } = useAdmin("services", "/admin/services");
  const [cats, setCats] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  useEffect(() => { if (data && !cats) setCats(structuredClone(data.categories)); }, [data, cats]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/services", { categories: cats.map((c) => ({ ...c, items: c.items.map((i) => ({ ...i, price: Number(i.price) || 0 })) })) });
      qc.invalidateQueries({ queryKey: ["services"] });
      qc.invalidateQueries({ queryKey: ["admin", "services"] });
      toast.success("Services & prices saved");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const move = (from, k, to) => {
    const it = cats[from].items[k];
    setCats(cats.map((o, j) => (j === from ? { ...o, items: o.items.filter((_, x) => x !== k) } : j === to ? { ...o, items: [...o.items, it] } : o)));
    toast.success(`${it.name || "Service"} moved to ${cats[to].label} — tap Save changes to publish`);
  };
  const addCat = () => { const id = `new-${cats.length + 1}`; setCats([...cats, { id, label: "", items: [] }]); setOpen(cats.length); };

  return (
    <div data-testid="admin-services">
      <PageHead eyebrow="Menu" title="Services & prices">
        <button className="btn gold" onClick={save} disabled={busy || !cats} data-testid="services-save-button">{busy ? <Loader2 size={16} className="spin" /> : "Save changes"}</button>
      </PageHead>
      <p className="muted-sm">Deposits for confirmed bookings are 50% of the price. Tick “From” for starting prices.</p>
      <FreshaSync onSynced={() => setCats(null)} />
      <ExcelTools testPrefix="services" title="Update prices with Excel" exportUrl="/admin/services/export" importUrl="/admin/services/import" filename="waxmoi-services.xlsx"
        text="Download every service (category, name, duration, price, from-price, description, Fresha link), change the prices you want, then upload it back. Only filled cells change. Rows without a Key are added as new services — type an existing category name for them. Changes go live immediately."
        onImported={async () => { await qc.invalidateQueries({ queryKey: ["admin", "services"] }); qc.invalidateQueries({ queryKey: ["services"] }); setCats(null); }} />
      {!cats ? <Loading /> : cats.map((c, i) => (
        <Category key={c.id} c={c} open={open === i} toggle={() => setOpen(open === i ? null : i)}
          onChange={(x) => setCats(cats.map((o, j) => (j === i ? x : o)))}
          onRemove={() => window.confirm(`Delete ${c.label}?`) && setCats(cats.filter((_, j) => j !== i))}
          targets={cats.map((o, j) => [j, o.label]).filter(([j]) => j !== i)} onMove={(k, to) => move(i, k, to)} />
      ))}
      {cats && <button className="btn light ad-add" onClick={addCat} data-testid="services-add-category"><Plus size={14} /> Add category</button>}
      <div className="ad-savebar"><button className="btn gold" onClick={save} disabled={busy || !cats} data-testid="services-save-bottom-button">Save changes</button></div>
    </div>
  );
}
